/**
 * Match all characters that need to be escaped in a string. Modified from
 * source to match single quotes instead of double.
 *
 * Source: https://github.com/douglascrockford/JSON-js/blob/master/json2.js
 */
// eslint-disable-next-line no-control-regex, no-misleading-character-class
const ESCAPABLE = /[\\\'\x00-\x1F\x7F-\x9F\u00AD\u0600-\u0604\u070F\u17B4\u17B5\u200C-\u200F\u2028-\u202F\u2060-\u206F\uFEFF\uFFF0-\uFFFF]/g;
/**
 * Map of characters to escape characters.
 */
const META_CHARS = new Map([
    ['\b', '\\b'],
    ['\t', '\\t'],
    ['\n', '\\n'],
    ['\f', '\\f'],
    ['\r', '\\r'],
    ['\'', '\\\''],
    ['"', '\\"'],
    ['\\', '\\\\'],
]);
/**
 * Escape any character into its literal JavaScript string.
 *
 * @param  {string} char
 * @return {string}
 */
function escapeChar(char) {
    return (META_CHARS.get(char) ||
        `\\u${`0000${char.charCodeAt(0).toString(16)}`.slice(-4)}`);
}
/**
 * Quote a string.
 */
function quoteString(str) {
    return `'${str.replace(ESCAPABLE, escapeChar)}'`;
}
/**
 * JavaScript reserved keywords.
 */
const RESERVED_WORDS = new Set(('break else new var case finally return void catch for switch while ' +
    'continue function this with default if throw delete in try ' +
    'do instanceof typeof abstract enum int short boolean export ' +
    'interface static byte extends long super char final native synchronized ' +
    'class float package throws const goto private transient debugger ' +
    'implements protected volatile double import public let yield').split(' '));
/**
 * Test for valid JavaScript identifier.
 */
const IS_VALID_IDENTIFIER = /^[A-Za-z_$][A-Za-z0-9_$]*$/;
/**
 * Check if a variable name is valid.
 */
function isValidVariableName(name) {
    return (typeof name === 'string' &&
        !RESERVED_WORDS.has(name) &&
        IS_VALID_IDENTIFIER.test(name));
}
/**
 * Quote JavaScript key access.
 */
function quoteKey(key, next) {
    return isValidVariableName(key) ? key : next(key);
}
/**
 * Serialize the path to a string.
 */
function stringifyPath(path, next) {
    let result = '';
    for (const key of path) {
        if (isValidVariableName(key))
            result += `.${key}`;
        else
            result += `[${next(key)}]`;
    }
    return result;
}

/**
 * Used in function stringification.
 */
/* istanbul ignore next */
const METHOD_NAMES_ARE_QUOTED = {
    ' ': function () {
        /* Empty. */
    },
}[' ']
    .toString()
    .charAt(0) === '"';
const FUNCTION_PREFIXES = {
    Function: 'function ',
    GeneratorFunction: 'function* ',
    AsyncFunction: 'async function ',
    AsyncGeneratorFunction: 'async function* ',
};
const METHOD_PREFIXES = {
    Function: '',
    GeneratorFunction: '*',
    AsyncFunction: 'async ',
    AsyncGeneratorFunction: 'async *',
};
const TOKENS_PRECEDING_REGEXPS = new Set(('case delete else in instanceof new return throw typeof void ' +
    ', ; : + - ! ~ & | ^ * / % < > ? =').split(' '));
/**
 * Track function parser usage.
 */
const USED_METHOD_KEY = new WeakSet();
/**
 * Stringify a function.
 */
const functionToString = (fn, space, next, key) => {
    const name = typeof key === 'string' ? key : undefined;
    // Track in function parser for object stringify to avoid duplicate output.
    if (name !== undefined)
        USED_METHOD_KEY.add(fn);
    return new FunctionParser(fn, space, next, name).stringify();
};
/**
 * Rewrite a stringified function to remove initial indentation.
 */
function dedentFunction(fnString) {
    let found;
    for (const line of fnString.split('\n').slice(1)) {
        const m = /^[\s\t]+/.exec(line);
        if (!m)
            return fnString; // Early exit without indent.
        const [str] = m;
        if (found === undefined)
            found = str;
        else if (str.length < found.length)
            found = str;
    }
    return found ? fnString.split(`\n${found}`).join('\n') : fnString;
}
/**
 * Function parser and stringify.
 */
class FunctionParser {
    constructor(fn, indent, next, key) {
        this.fn = fn;
        this.indent = indent;
        this.next = next;
        this.key = key;
        this.pos = 0;
        this.hadKeyword = false;
        this.fnString = Function.prototype.toString.call(fn);
        this.fnType = fn.constructor.name;
        this.keyQuote = key === undefined ? '' : quoteKey(key, next);
        this.keyPrefix =
            key === undefined ? '' : `${this.keyQuote}:${indent ? ' ' : ''}`;
        this.isMethodCandidate =
            // eslint-disable-next-line no-mixed-operators
            key === undefined ? false : this.fn.name === '' || this.fn.name === key;
    }
    stringify() {
        const value = this.tryParse();
        // If we can't stringify this function, return a void expression; for
        // bonus help with debugging, include the function as a string literal.
        if (!value)
            return `${this.keyPrefix}void ${this.next(this.fnString)}`;
        return dedentFunction(value);
    }
    getPrefix() {
        if (this.isMethodCandidate && !this.hadKeyword)
            return METHOD_PREFIXES[this.fnType] + this.keyQuote;
        return this.keyPrefix + FUNCTION_PREFIXES[this.fnType];
    }
    tryParse() {
        if (this.fnString[this.fnString.length - 1] !== '}') {
            // Must be an arrow function.
            return this.keyPrefix + this.fnString;
        }
        // Attempt to remove function prefix.
        if (this.fn.name) {
            const result = this.tryStrippingName();
            if (result)
                return result;
        }
        // Support class expressions.
        const prevPos = this.pos;
        if (this.consumeSyntax() === 'class')
            return this.fnString;
        this.pos = prevPos;
        if (this.tryParsePrefixTokens()) {
            const result = this.tryStrippingName();
            if (result)
                return result;
            let offset = this.pos;
            switch (this.consumeSyntax('WORD_LIKE')) {
                case 'WORD_LIKE':
                    if (this.isMethodCandidate && !this.hadKeyword)
                        offset = this.pos;
                // eslint-disable-next-line no-fallthrough
                case '()':
                    if (this.fnString.substr(this.pos, 2) === '=>')
                        return this.keyPrefix + this.fnString;
                    this.pos = offset;
                // eslint-disable-next-line no-fallthrough
                case '"':
                case '\'':
                case '[]':
                    return this.getPrefix() + this.fnString.substr(this.pos);
            }
        }
    }
    /**
     * Attempt to parse the function from the current position by first stripping
     * the function's name from the front. This is not a fool-proof method on all
     * JavaScript engines, but yields good results on Node.js 4 (and slightly
     * less good results on Node.js 6 and 8).
     */
    tryStrippingName() {
        if (METHOD_NAMES_ARE_QUOTED) {
            // ... then this approach is unnecessary and yields false positives.
            return;
        }
        let start = this.pos;
        const prefix = this.fnString.substr(this.pos, this.fn.name.length);
        if (prefix === this.fn.name) {
            this.pos += prefix.length;
            if (this.consumeSyntax() === '()' &&
                this.consumeSyntax() === '{}' &&
                this.pos === this.fnString.length) {
                // Don't include the function's name if it will be included in the
                // prefix, or if it's invalid as a name in a function expression.
                if (this.isMethodCandidate || !isValidVariableName(prefix))
                    start += prefix.length;
                return this.getPrefix() + this.fnString.substr(start);
            }
        }
        this.pos = start;
    }
    /**
     * Attempt to advance the parser past the keywords expected to be at the
     * start of this function's definition. This method sets `this.hadKeyword`
     * based on whether or not a `function` keyword is consumed.
     */
    tryParsePrefixTokens() {
        let posPrev = this.pos;
        this.hadKeyword = false;
        switch (this.fnType) {
            case 'AsyncFunction':
                if (this.consumeSyntax() !== 'async')
                    return false;
                posPrev = this.pos;
            // eslint-disable-next-line no-fallthrough
            case 'Function':
                if (this.consumeSyntax() === 'function')
                    this.hadKeyword = true;
                else
                    this.pos = posPrev;
                return true;
            case 'AsyncGeneratorFunction':
                if (this.consumeSyntax() !== 'async')
                    return false;
            // eslint-disable-next-line no-fallthrough
            case 'GeneratorFunction':
                // eslint-disable-next-line no-case-declarations
                let token = this.consumeSyntax();
                if (token === 'function') {
                    token = this.consumeSyntax();
                    this.hadKeyword = true;
                }
                return token === '*';
        }
    }
    /**
     * Advance the parser past one element of JavaScript syntax. This could be a
     * matched pair of delimiters, like braces or parentheses, or an atomic unit
     * like a keyword, variable, or operator. Return a normalized string
     * representation of the element parsed--for example, returns '{}' for a
     * matched pair of braces. Comments and whitespace are skipped.
     *
     * (This isn't a full parser, so the token scanning logic used here is as
     * simple as it can be. As a consequence, some things that are one token in
     * JavaScript, like decimal number literals or most multi-character operators
     * like '&&', are split into more than one token here. However, awareness of
     * some multi-character sequences like '=>' is necessary, so we match the few
     * of them that we care about.)
     */
    consumeSyntax(wordLikeToken) {
        const m = this.consumeMatch(/^(?:([A-Za-z_0-9$\xA0-\uFFFF]+)|=>|\+\+|\-\-|.)/);
        if (!m)
            return;
        const [token, match] = m;
        this.consumeWhitespace();
        if (match)
            return wordLikeToken || match;
        switch (token) {
            case '(':
                return this.consumeSyntaxUntil('(', ')');
            case '[':
                return this.consumeSyntaxUntil('[', ']');
            case '{':
                return this.consumeSyntaxUntil('{', '}');
            case '`':
                return this.consumeTemplate();
            case '"':
                return this.consumeRegExp(/^(?:[^\\"]|\\.)*"/, '"');
            case '\'':
                return this.consumeRegExp(/^(?:[^\\']|\\.)*'/, '\'');
        }
        return token;
    }
    consumeSyntaxUntil(startToken, endToken) {
        let isRegExpAllowed = true;
        for (;;) {
            const token = this.consumeSyntax();
            if (token === endToken)
                return startToken + endToken;
            if (!token || token === ')' || token === ']' || token === '}')
                return;
            if (token === '/' &&
                isRegExpAllowed &&
                this.consumeMatch(/^(?:\\.|[^\\\/\n[]|\[(?:\\.|[^\]])*\])+\/[a-z]*/)) {
                isRegExpAllowed = false;
                this.consumeWhitespace();
            }
            else {
                isRegExpAllowed = TOKENS_PRECEDING_REGEXPS.has(token);
            }
        }
    }
    consumeMatch(re) {
        const m = re.exec(this.fnString.substr(this.pos));
        if (m)
            this.pos += m[0].length;
        return m;
    }
    /**
     * Advance the parser past an arbitrary regular expression. Return `token`,
     * or the match object of the regexp.
     */
    consumeRegExp(re, token) {
        const m = re.exec(this.fnString.substr(this.pos));
        if (!m)
            return;
        this.pos += m[0].length;
        this.consumeWhitespace();
        return token;
    }
    /**
     * Advance the parser past a template string.
     */
    consumeTemplate() {
        for (;;) {
            this.consumeMatch(/^(?:[^`$\\]|\\.|\$(?!{))*/);
            if (this.fnString[this.pos] === '`') {
                this.pos++;
                this.consumeWhitespace();
                return '`';
            }
            if (this.fnString.substr(this.pos, 2) === '${') {
                this.pos += 2;
                this.consumeWhitespace();
                if (this.consumeSyntaxUntil('{', '}'))
                    continue;
            }
            return;
        }
    }
    /**
     * Advance the parser past any whitespace or comments.
     */
    consumeWhitespace() {
        this.consumeMatch(/^(?:\s|\/\/.*|\/\*[^]*?\*\/)*/);
    }
}

/**
 * Stringify an array of values.
 */
const arrayToString = (array, space, next) => {
    // Map array values to their stringified values with correct indentation.
    const values = array
        .map((value, index) => {
        const result = next(value, index);
        if (result === undefined)
            return String(result);
        return space + result.split('\n').join(`\n${space}`);
    })
        .join(space ? ',\n' : ',');
    // eslint-disable-next-line no-mixed-operators
    const eol = space && values ? '\n' : '';
    return `[${eol}${values}${eol}]`;
};

/**
 * Transform an object into a string.
 */
const objectToString = (value, space, next, key) => {
    // Support buffer in all environments.
    // @ts-ignore
    // if (typeof Buffer === 'function' && Buffer.isBuffer(value))
    //   return `Buffer.from(${next(value.toString('base64'))}, 'base64')`
    // Support `global` under test environments that don't print `[object global]`.
    // @ts-ignore
    if (typeof global === 'object' && value === global)
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        return globalToString(value, space, next);
    // Use the internal object string to select stringify method.
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const toString = OBJECT_TYPES[Object.prototype.toString.call(value)];
    return toString ? toString(value, space, next, key) : undefined;
};
/**
 * Stringify an object of keys and values.
 */
const rawObjectToString = (obj, indent, next, key) => {
    const eol = indent ? '\n' : '';
    const space = indent ? ' ' : '';
    // Iterate over object keys and concat string together.
    const values = Object.keys(obj)
        .reduce((values, key) => {
        const fn = obj[key];
        const result = next(fn, key);
        // Omit `undefined` object entries.
        if (result === undefined)
            return values;
        // String format the value data.
        const value = result.split('\n').join(`\n${indent}`);
        // Skip `key` prefix for function parser.
        if (USED_METHOD_KEY.has(fn)) {
            values.push(`${indent}${value}`);
            return values;
        }
        values.push(`${indent}${quoteKey(key, next)}:${space}${value}`);
        return values;
    }, [])
        .join(`,${eol}`);
    // Avoid new lines in an empty object.
    if (values === '')
        return '{}';
    return `{${eol}${values}${eol}}`;
};
/**
 * Stringify global variable access.
 */
const globalToString = (value, space, next) => {
    return `Function(${next('return this')})()`;
};
/**
 * Convert JavaScript objects into strings.
 */
const OBJECT_TYPES = {
    '[object Array]': arrayToString,
    '[object Object]': rawObjectToString,
    '[object Error]': (error, space, next) => {
        return `new Error(${next(error.message)})`;
    },
    '[object Date]': (date) => {
        return `new Date(${date.getTime()})`;
    },
    '[object String]': (str, space, next) => {
        return `new String(${next(str.toString())})`;
    },
    '[object Number]': (num) => {
        return `new Number(${num})`;
    },
    '[object Boolean]': (bool) => {
        return `new Boolean(${bool})`;
    },
    '[object Set]': (set, space, next) => {
        return `new Set(${next(Array.from(set))})`;
    },
    '[object Map]': (map, space, next) => {
        return `new Map(${next(Array.from(map))})`;
    },
    '[object RegExp]': String,
    '[object global]': globalToString,
    '[object Window]': globalToString,
};

/**
 * Stringify primitive values.
 */
const PRIMITIVE_TYPES = {
    string: quoteString,
    number: (value) => (Object.is(value, -0) ? '-0' : String(value)),
    boolean: String,
    symbol: (value, space, next) => {
        const key = Symbol.keyFor(value);
        if (key !== undefined)
            return `Symbol.for(${next(key)})`;
        // ES2018 `Symbol.description`.
        return `Symbol(${next(value.description)})`;
    },
    bigint: (value, space, next) => {
        return `BigInt(${next(String(value))})`;
    },
    undefined: String,
    object: objectToString,
    function: functionToString,
};
/**
 * Stringify a value recursively.
 */
const toString = (value, space, next, key) => {
    if (value === null)
        return 'null';
    return PRIMITIVE_TYPES[typeof value](value, space, next, key);
};

/**
 * Root path node.
 */
const ROOT_SENTINEL = Symbol('root');
/**
 * Stringify any JavaScript value.
 */
function stringify(value, replacer, indent, options = {}) {
    const space = typeof indent === 'string' ? indent : ' '.repeat(indent || 0);
    const path = [];
    const stack = new Set();
    const tracking = new Map();
    const unpack = new Map();
    let valueCount = 0;
    const { maxDepth = 100, references = false, skipUndefinedProperties = false, maxValues = 100000, } = options;
    // Wrap replacer function to support falling back on supported stringify.
    const valueToString = replacerToString(replacer);
    // Every time you call `next(value)` execute this function.
    const onNext = (value, key) => {
        if (++valueCount > maxValues)
            return;
        if (skipUndefinedProperties && value === undefined)
            return;
        if (path.length > maxDepth)
            return;
        // An undefined key is treated as an out-of-band "value".
        if (key === undefined)
            return valueToString(value, space, onNext, key);
        path.push(key);
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const result = builder(value, key === ROOT_SENTINEL ? undefined : key);
        path.pop();
        return result;
    };
    const builder = references
        ? (value, key) => {
            if (value !== null &&
                (typeof value === 'object' ||
                    typeof value === 'function' ||
                    typeof value === 'symbol')) {
                // Track nodes to restore later.
                if (tracking.has(value)) {
                    unpack.set(path.slice(1), tracking.get(value));
                    // Use `undefined` as temporaray stand-in for referenced nodes
                    return valueToString(undefined, space, onNext, key);
                }
                // Track encountered nodes.
                tracking.set(value, path.slice(1));
            }
            return valueToString(value, space, onNext, key);
        }
        : (value, key) => {
            // Stop on recursion.
            if (stack.has(value))
                return;
            stack.add(value);
            const result = valueToString(value, space, onNext, key);
            stack.delete(value);
            return result;
        };
    const result = onNext(value, ROOT_SENTINEL);
    // Attempt to restore circular references.
    if (unpack.size) {
        const sp = space ? ' ' : '';
        const eol = space ? '\n' : '';
        let wrapper = `var x${sp}=${sp}${result};${eol}`;
        for (const [key, value] of unpack.entries()) {
            const keyPath = stringifyPath(key, onNext);
            const valuePath = stringifyPath(value, onNext);
            wrapper += `x${keyPath}${sp}=${sp}x${valuePath};${eol}`;
        }
        return `(function${sp}()${sp}{${eol}${wrapper}return x;${eol}}())`;
    }
    return result;
}
/**
 * Create `toString()` function from replacer.
 */
function replacerToString(replacer) {
    if (!replacer)
        return toString;
    return (value, space, next, key) => {
        return replacer(value, space, (value) => toString(value, space, next, key), key);
    };
}

export { stringify };
